/**
 * V4 企业看板真实 API（接 backend M14-M17）。
 *
 * 契约：docs/archive/v4/V4-API-dashboard.yaml
 * 后端 endpoint:
 *   GET /api/dashboard/classes/                         M14 list
 *   GET /api/dashboard/classes/{id}/                    M14 retrieve（聚合总览）
 *   GET /api/dashboard/classes/{id}/heatmap/?sort=...   M15
 *   GET /api/dashboard/classes/{id}/risks/              M16
 *   GET /api/dashboard/classes/{id}/training-recommendation/?refresh=true M17
 *
 * 权限（前端只是 UX 层提示；后端是真实关卡）：
 * - 学员 → 全 403（不应进入看板）
 * - 讲师 → list 仅自己授课的班；training-recommendation **403**
 * - enterprise_admin / admin / superadmin → 看本企业 / 全部
 */
import client from './client';
import { downloadBlobReport } from './_download';

// 后端 VALID_HEATMAP_SORTS = ("score_desc", "score_asc", "name") 的镜像。
// 加新值时, 后端 services/dashboard.py 与本常量同步改。
export const HEATMAP_SORTS = {
  SCORE_DESC: 'score_desc',
  SCORE_ASC: 'score_asc',
  NAME: 'name',
};

/** 把 axios 错误翻成中文用户态文案。看板各页错误态共用。 */
export function readableApiError(err, fallback = '加载失败') {
  const code = err?.response?.status;
  if (code === 404) return '资源不存在或您无权访问';
  if (code === 403) return '无权访问';
  if (code === 401) return '请重新登录';
  return err?.response?.data?.detail || fallback;
}

/** 班级头部副标题: '企业 · 行业' 共用。 */
export function formatClassSubtitle(cls) {
  const ent = cls?.enterprise || {};
  return `${ent.name || ''} · ${ent.industry || '未指定行业'}`;
}

export const listClasses = (params) =>
  client.get('/dashboard/classes/', { params }).then((r) => r.data);

export const listEnterprises = (params) =>
  client.get('/dashboard/enterprises/', { params }).then((r) => r.data);

/**
 * default-class-flat:解析"调用者的默认班级",用于 dashboard 入口直跳。
 *  - 讲师 → 自己授课的第一个班
 *  - 企业管理员 → 本企业第一个班(信号自动建的"默认班级")
 *  - admin / superadmin → 404(他们看跨企业,继续走选择器)
 *  - 学员 → 403(后端 IsInstructorOrAbove 拦截)
 */
export const getDefaultClass = () =>
  client.get('/dashboard/classes/default/').then((r) => r.data);

export const getClassDashboard = (id) =>
  client.get(`/dashboard/classes/${id}/`).then((r) => r.data);

export const getClassHeatmap = (id, { sort } = {}) =>
  client.get(`/dashboard/classes/${id}/heatmap/`, { params: sort ? { sort } : {} })
    .then((r) => r.data);

export const getClassRisks = (id) =>
  client.get(`/dashboard/classes/${id}/risks/`).then((r) => r.data);

export const getTrainingRecommendation = (id, { refresh } = {}) =>
  client.get(`/dashboard/classes/${id}/training-recommendation/`, {
    params: refresh ? { refresh: 'true' } : {},
  }).then((r) => r.data);

// 作答质量（水填检测）— 后端 gate 到 enterprise_admin+，讲师 403（spec §5）。
export const getClassResponseQuality = (id) =>
  client.get(`/dashboard/classes/${id}/response-quality/`).then((r) => r.data);

// ── enterprise-scoped（无班级企业全员看板，class-stream Phase B §4.4）──────────
// 与上面 class 版一一对应；前端 5 个 page 按 scope prop 二选一调用。

export const getEnterpriseDashboard = (id) =>
  client.get(`/dashboard/enterprises/${id}/`).then((r) => r.data);

export const getEnterpriseHeatmap = (id, { sort } = {}) =>
  client.get(`/dashboard/enterprises/${id}/heatmap/`, { params: sort ? { sort } : {} })
    .then((r) => r.data);

export const getEnterpriseRisks = (id) =>
  client.get(`/dashboard/enterprises/${id}/risks/`).then((r) => r.data);

export const getEnterpriseTrainingRecommendation = (id, { refresh } = {}) =>
  client.get(`/dashboard/enterprises/${id}/training-recommendation/`, {
    params: refresh ? { refresh: 'true' } : {},
  }).then((r) => r.data);

export const getEnterpriseResponseQuality = (id) =>
  client.get(`/dashboard/enterprises/${id}/response-quality/`).then((r) => r.data);

// ── PDF 下载（class-stream Phase C §C6 / C8）─────────────────────────────────

export const downloadClassPdf = (classId) =>
  downloadBlobReport(`/dashboard/classes/${classId}/download/pdf/`, { fallbackName: '班级报告.pdf' });

export const downloadEnterprisePdf = (enterpriseId) =>
  downloadBlobReport(`/enterprises/${enterpriseId}/download/pdf/`, { fallbackName: '企业报告.pdf' });
